export type ArticleTone = "red" | "blue" | "navy";

export type ArticleSection = {
  id: string;
  title: string;
  paragraphs: string[];
  bullets?: string[];
  callout?: string;
};

export type ArticleSource = {
  label: string;
  organization: string;
  href: string;
};

export type KnowledgeArticle = {
  slug: string;
  category: string;
  tone: ArticleTone;
  title: string;
  seoTitle: string;
  shortTitle: string;
  description: string;
  readMinutes: number;
  updated: string;
  featured: boolean;
  outcomes: string[];
  sections: ArticleSection[];
  sources: ArticleSource[];
  relatedSlugs: string[];
};

export const KNOWLEDGE_BASE_PATH = "/rehberler/";
export const SITE_URL = "https://amerikolay.com";

export function articlePath(article: KnowledgeArticle) {
  return `${KNOWLEDGE_BASE_PATH}${article.slug}/`;
}

export const articles: KnowledgeArticle[] = [
  {
    slug: "green-card-cekilisi-basvuru-rehberi",
    category: "Green Card",
    tone: "red",
    title: "Green Card Çekilişi Başvuru Rehberi: Hatasız Hazırlık Kontrol Listesi",
    seoTitle: "Green Card Başvurusu: Hatasız Hazırlık Rehberi",
    shortTitle: "Green Card başvurusuna doğru hazırlan",
    description: "Green Card çekilişi için resmi başvuru adresi, tek başvuru kuralı, fotoğraf, aile bilgileri ve confirmation number adımlarını öğren.",
    readMinutes: 7,
    updated: "22 Ağustos 2026",
    featured: true,
    outcomes: [
      "Resmi başvuru ile hazırlık hizmetini ayırt edebileceksin",
      "Başvuru öncesi bilgi ve fotoğraf kontrolünü tamamlayacaksın",
      "Confirmation Number ve sonuç kontrolü konusunda hata riskini azaltacaksın",
    ],
    sections: [
      {
        id: "resmi-surec",
        title: "1. Önce resmi süreci doğru tanı",
        paragraphs: [
          "Diversity Visa (DV) Programı ABD Dışişleri Bakanlığı tarafından yürütülür. Kayıt yalnızca Bakanlığın ilan ettiği sınırlı dönem içinde, resmi Electronic Diversity Visa sistemi üzerinden elektronik olarak yapılır. Geç başvuru ve kâğıt başvuru kabul edilmez.",
          "Resmi DV kaydı ücretsizdir. Amerikolay gibi özel hizmetler kontrol listesi, fotoğraf denetimi ve hatırlatma sunabilir; ancak resmi başvuru sitesi değildir ve seçilme garantisi veremez.",
        ],
        callout: "Güvenli kural: Resmi kayıt için adres çubuğunda dvprogram.state.gov alan adını görmeden kişisel bilgi girme.",
      },
      {
        id: "hazirlik",
        title: "2. Başvuru ekranı açılmadan önce hazırla",
        paragraphs: [
          "Her program yılı için yayımlanan İngilizce DV talimatları esas alınır. Uygun ülkeler, eğitim veya iş deneyimi şartları ve fotoğraf kuralları program yılına göre kontrol edilmelidir. Geçmiş yılların ekran görüntülerine güvenme.",
          "Formu doldururken pasaport ve nüfus kayıtlarındaki yazımlarla tutarlı ol. Mevcut eş ve uygun çocuklarla ilgili bilgilerin eksik ya da yanlış girilmesi ciddi sonuçlar doğurabilir.",
        ],
        bullets: [
          "Ad, soyad, doğum yeri ve doğum tarihini resmi belgelerden doğrula.",
          "Eş ve çocuk bilgilerini güncel program talimatına göre hazırla.",
          "Son altı ay içinde çekilmiş, güncel DV fotoğraf şartlarına uygun bir fotoğraf kullan.",
          "Formu acele etmeden tamamlayabileceğin güvenli bir cihaz ve bağlantı seç.",
        ],
      },
      {
        id: "tek-basvuru",
        title: "3. Tek başvuru kuralını ihlal etme",
        paragraphs: [
          "ABD Dışişleri Bakanlığı aynı kişi için her kayıt döneminde yalnızca bir girişe izin verir. Bir kişi adına birden fazla kayıt gönderilmesi, o kişinin tüm girişlerinin diskalifiye edilmesine yol açabilir.",
          "Evli çiftlerde, her iki eş de ayrı ayrı uygunluk şartlarını karşılıyorsa kendi adına birer kayıt gönderebilir; her başvuruda eş ve uygun çocuklar talimatlara göre doğru şekilde listelenmelidir.",
        ],
      },
      {
        id: "confirmation-number",
        title: "4. Confirmation Number'ı kendin sakla",
        paragraphs: [
          "Başarılı gönderimden sonra ekranda benzersiz bir Confirmation Number görünür. Bu numara sonuç kontrolü ve seçilmen halinde sonraki talimatlar için gereklidir. Ekranı PDF olarak kaydet, güvenli bir parola kasasında sakla ve kontrolü başkasına bırakma.",
          "Seçilme sonucu yalnızca resmi Entrant Status Check ekranından öğrenilir. Dışişleri Bakanlığı seçilenlere e-posta veya mektupla kazandınız bildirimi göndermez. Seçilmek de otomatik olarak vize verildiği anlamına gelmez; sonraki uygunluk, form, belge ve mülakat adımları ayrıca tamamlanır.",
        ],
      },
      {
        id: "son-kontrol",
        title: "5. Göndermeden önce 60 saniyelik son kontrol",
        paragraphs: [
          "Gönderim düğmesine basmadan önce formdaki kişi sayısını, isim yazımlarını, medeni hâli, çocuk bilgilerini ve fotoğrafı bir kez daha kontrol et. Son ekrandaki kayıt bilgileri senin sorumluluğundadır.",
        ],
        bullets: [
          "Bu kayıt döneminde adıma daha önce başvuru gönderilmedi.",
          "Aile bilgilerim güncel talimatlarla uyumlu.",
          "Fotoğrafım yeniden kullanılmamış ve güncel teknik şartlara uygun.",
          "Gönderimden sonra Confirmation Number'ı ben saklayacağım.",
        ],
      },
    ],
    sources: [
      {
        label: "Diversity Visa Program — Submit an Entry",
        organization: "U.S. Department of State",
        href: "https://travel.state.gov/content/travel/en/us-visas/immigrate/diversity-visa-program-entry/diversity-visa-submit-entry1.html",
      },
      {
        label: "Diversity Visa Instructions",
        organization: "U.S. Department of State",
        href: "https://travel.state.gov/content/travel/en/us-visas/immigrate/diversity-visa-program-entry/diversity-visa-instructions.html",
      },
      {
        label: "Selection of Applicants",
        organization: "U.S. Department of State",
        href: "https://travel.state.gov/content/travel/en/us-visas/immigrate/diversity-visa-program-entry/diversity-visa-submit-entry1/diversity-visa-selection-of-applicants.html",
      },
      {
        label: "Visa Photo Requirements",
        organization: "U.S. Department of State",
        href: "https://travel.state.gov/content/travel/en/us-visas/visa-information-resources/photos.html",
      },
    ],
    relatedSlugs: ["gocmenlik-dolandiriciliklarindan-korunma", "abd-vize-turleri-karsilastirma"],
  },
  {
    slug: "amerika-is-basvurusu-cv-rehberi",
    category: "Amerika'da İş",
    tone: "blue",
    title: "Amerika'da İş Başvurusu: ABD Formatında CV ve Sponsor Arama Rehberi",
    seoTitle: "Amerika'da İş: ABD CV ve Sponsor Rehberi",
    shortTitle: "ABD formatında CV ve iş başvurusu",
    description: "Amerika'da iş başvurusu için ATS uyumlu résumé hazırlama, sponsor geçmişini yorumlama ve haftalık başvuru sistemi kurma rehberi.",
    readMinutes: 8,
    updated: "22 Ağustos 2026",
    featured: true,
    outcomes: [
      "ABD iş ilanlarına uygun, sade ve ölçülebilir bir résumé hazırlayacaksın",
      "Sponsor geçmişi ile güncel sponsor isteğini birbirine karıştırmayacaksın",
      "Takip edilebilir bir iş başvurusu rutini kuracaksın",
    ],
    sections: [
      {
        id: "hedef",
        title: "1. Her şeyden önce tek bir iş hedefi seç",
        paragraphs: [
          "Amerika'da iş ararken aynı CV ile onlarca farklı role başvurmak yerine birincil unvan, seviye ve sektör belirle. Örneğin “software engineer” çok geniştir; “backend .NET engineer — health tech” daha net bir hedef oluşturur.",
          "İlanın çalışma izni sorularını dürüst cevapla. Bir işverenin geçmişte H-1B veya başka bir süreç yürütmüş olması, yeni aday için sponsorluk yapacağı anlamına gelmez. İlandaki güncel dil ve işe alım ekibinin teyidi belirleyicidir.",
        ],
      },
      {
        id: "resume-formati",
        title: "2. ATS'nin okuyabileceği sade bir résumé kullan",
        paragraphs: [
          "ABD Çalışma Bakanlığı destekli CareerOneStop, özgeçmişin kolay okunmasını ve en önemli bilgilerin hızla bulunmasını önerir. Tek sütunlu düzen; Work Experience, Education ve Skills gibi standart İngilizce başlıklar, hem insan okuyucular hem de aday takip sistemleri için güvenli bir başlangıçtır.",
          "Fotoğraf, yaş, medeni hâl, tam ev adresi, kimlik numarası ve ilgisiz kişisel bilgiler ekleme. İletişim bölümünde ad, profesyonel e-posta, telefon, şehir/ülke ve güncel LinkedIn ya da portföy bağlantısı yeterlidir.",
        ],
        bullets: [
          "Çoğu aday için 1 sayfa; deneyim gerçekten gerektiriyorsa en fazla 2 sayfa.",
          "Tablo, metin kutusu, çoklu sütun, logo ve yoğun grafikten kaçın.",
          "İlan istemedikçe okunabilir bir PDF gönder; dosya adını Ad-Soyad-Unvan.pdf yap.",
          "Her başvuruda ilandaki gerçek beceri ve terimlerle résumé'yi uyumlaştır.",
        ],
      },
      {
        id: "basari-maddeleri",
        title: "3. Görev değil, ölçülebilir sonuç yaz",
        paragraphs: [
          "Her deneyim maddesini güçlü bir eylem fiiliyle başlat; ne yaptığını, hangi ölçekte yaptığını ve sonucunu mümkün olduğunca sayıyla göster. “Responsible for reports” yerine “Automated 12 monthly reports, reducing preparation time by 35%” daha güçlü bir kanıttır.",
          "Türkiye'deki unvanını ABD'de anlaşılır karşılığıyla yazabilirsin; ancak kıdemini veya sorumluluğunu büyütme. Doğrulanabilirlik güven oluşturur.",
        ],
      },
      {
        id: "sponsor-arastirmasi",
        title: "4. Sponsor araştırmasını kanıt olarak değil, sinyal olarak kullan",
        paragraphs: [
          "ABD Çalışma Bakanlığı, H-1B LCA ve çeşitli yabancı işgücü programlarına ilişkin kamuya açık performans ve disclosure verileri yayımlar. Bu veriler hangi işverenlerin geçmişte süreç yürüttüğünü gösterebilir; açık pozisyonu, bütçeyi veya gelecekteki kararı garanti etmez.",
          "Bir işvereni değerlendirirken geçmiş kayıt, ilandaki sponsorship ifadesi, rolün uzmanlık seviyesi, maaş aralığı ve işe alım ekibinin yanıtını birlikte oku.",
        ],
        callout: "İlanda “must be authorized to work without current or future sponsorship” yazıyorsa, geçmiş sponsor verisi bu güncel şartı geçersiz kılmaz.",
      },
      {
        id: "haftalik-sistem",
        title: "5. Haftalık başvuru sistemi kur",
        paragraphs: [
          "Başarıyı yalnızca gönderilen başvuru sayısıyla ölçme. Hedef şirket, doğru rol, uyarlanmış résumé, takip tarihi ve gelen yanıtı tek tabloda tut. Böylece hangi unvanların, şehirlerin ve résumé sürümlerinin sonuç verdiğini görebilirsin.",
        ],
        bullets: [
          "Pazartesi: 15 hedef şirket ve yeni ilan taraması.",
          "Salı–Perşembe: günde 3–5 yüksek uyumlu, özelleştirilmiş başvuru.",
          "Cuma: uygun kişilere kısa networking mesajları ve başvuru takibi.",
          "Hafta sonu: dönüşüm oranlarını incele; résumé ve hedefi güncelle.",
        ],
      },
    ],
    sources: [
      {
        label: "Résumé Guide — Formatting",
        organization: "CareerOneStop / U.S. Department of Labor",
        href: "https://www.careeronestop.org/JobSearch/Resumes/ResumeGuide/formatting.aspx",
      },
      {
        label: "Résumé Guide — Work Experience",
        organization: "CareerOneStop / U.S. Department of Labor",
        href: "https://www.careeronestop.org/JobSearch/Resumes/ResumeGuide/work-experience.aspx",
      },
      {
        label: "Foreign Labor Certification Performance Data",
        organization: "U.S. Department of Labor",
        href: "https://www.dol.gov/agencies/eta/foreign-labor/performance",
      },
      {
        label: "Working in the United States",
        organization: "USCIS",
        href: "https://www.uscis.gov/working-in-the-united-states",
      },
    ],
    relatedSlugs: ["abd-vize-turleri-karsilastirma", "vize-bulteni-priority-date-okuma"],
  },
  {
    slug: "abd-vize-turleri-karsilastirma",
    category: "Vize Rehberi",
    tone: "navy",
    title: "ABD Vize Türleri: B1/B2, F-1, J-1, H-1B, L-1, O-1 ve Göçmen Vizeler",
    seoTitle: "ABD Vize Türleri: B1/B2, F-1, H-1B ve Dahası",
    shortTitle: "ABD vize türlerini karşılaştır",
    description: "ABD vize türlerini seyahat amacına göre karşılaştır: ziyaret, eğitim, değişim, çalışma ve göçmenlik kategorileri arasındaki temel farklar.",
    readMinutes: 9,
    updated: "22 Ağustos 2026",
    featured: true,
    outcomes: [
      "Göçmen ve göçmen olmayan vize ayrımını anlayacaksın",
      "Seyahat amacını en yakın resmi kategoriyle eşleştireceksin",
      "İşveren dilekçesi veya okul kaydı gibi ön koşulları araştırabileceksin",
    ],
    sections: [
      {
        id: "temel-ayrim",
        title: "1. İlk ayrım: geçici kalış mı, kalıcı göç mü?",
        paragraphs: [
          "ABD vize sistemi genel olarak geçici kalış amaçlı nonimmigrant vizeler ile kalıcı göç amaçlı immigrant vizeler arasında ayrılır. Doğru kategori; seyahat amacına, kişisel koşullara ve bazı kategorilerde ABD'deki okul, program, aile üyesi ya da işveren sürecine bağlıdır.",
          "Bir kategorinin adını bilmek uygun olduğun anlamına gelmez. Başvuru sahibi ilgili şartları göstermeli; uygunluk ve vize kararı yetkili ABD makamlarınca verilir.",
        ],
      },
      {
        id: "ziyaret",
        title: "2. Ziyaret: B-1 ve B-2",
        paragraphs: [
          "B-1 geçici iş ziyareti; iş ortaklarıyla görüşme, konferansa katılma veya sözleşme müzakeresi gibi sınırlı faaliyetler için kullanılabilir. B-2 turizm, tatil, aile/arkadaş ziyareti ve belirli tıbbi amaçlar içindir.",
          "Ziyaretçi vizesi ABD'de normal bir işe girme ve çalışma izni değildir. Asıl amaç çalışmaksa, çalışma amacına uygun kategori araştırılmalıdır.",
        ],
      },
      {
        id: "egitim-degisim",
        title: "3. Eğitim ve değişim: F-1, M-1 ve J-1",
        paragraphs: [
          "F-1 akademik eğitim, M-1 mesleki eğitim için kullanılan öğrenci kategorileridir. Kabul edilen okulun gerekli kayıt ve SEVIS işlemlerini tamamlaması gerekir. J-1 ise onaylı değişim programlarına katılan öğrenci, stajyer, araştırmacı ve başka katılımcı grupları kapsayabilir.",
          "Çalışma imkânları kategorinin kendi kurallarıyla sınırlıdır. Öğrenci veya değişim statüsünü genel çalışma izni gibi değerlendirme.",
        ],
      },
      {
        id: "gecici-calisma",
        title: "4. Geçici çalışma: H, L, O ve diğerleri",
        paragraphs: [
          "H-1B uzmanlık meslekleri; L-1 şirket içi transfer; O-1 bilim, sanat, eğitim, iş veya atletizm alanında olağanüstü yetenek ya da başarı gibi farklı amaçlara yöneliktir. H-2A ve H-2B ise belirli geçici veya mevsimlik işgücü ihtiyaçları için ayrı süreçlerdir.",
          "Birçok çalışma kategorisinde vize başvurusundan önce ABD'li işverenin Department of Labor ve/veya USCIS tarafında işlem yürütmesi gerekir. Süreç, kategoriye göre değişir.",
        ],
        callout: "“Sponsorlu iş” tek bir vize adı değildir. İşin niteliği, adayın geçmişi ve işverenin yürütebildiği süreç birlikte değerlendirilir.",
      },
      {
        id: "gocmen-vizeler",
        title: "5. Göçmen vizeleri: aile, istihdam ve Diversity Visa",
        paragraphs: [
          "Kalıcı oturuma yönelik göçmen vize yolları arasında ABD vatandaşı veya Green Card sahibi belirli aile üyeleri üzerinden kategoriler, EB-1/EB-2/EB-3 gibi istihdam temelli tercihler, yatırımcı kategorileri ve Diversity Visa bulunur.",
          "Tercih kategorilerinin bazılarında yıllık sayısal sınırlar vardır. Bu nedenle onaylanmış dilekçe sonrasında bile öncelik tarihi ve Vize Bülteni bekleyişi söz konusu olabilir.",
        ],
      },
      {
        id: "karar-agaci",
        title: "6. Araştırmaya bu beş soruyla başla",
        paragraphs: [
          "Vize seçimini popülerliğe göre değil, gerçek amacına göre daralt. Karmaşık veya birden fazla olası yolun bulunduğu durumlarda yetkili bir göçmenlik avukatından kişisel değerlendirme almak en güvenli yaklaşımdır.",
        ],
        bullets: [
          "ABD'ye neden gitmek istiyorum: ziyaret, eğitim, iş, aile, yatırım?",
          "Planlanan kalış geçici mi, kalıcı mı?",
          "ABD'de işveren, okul, program veya uygun aile sponsoru var mı?",
          "Kategori için vize başvurusundan önce dilekçe/onay gerekiyor mu?",
          "Resmi kategori sayfasındaki güncel şartları karşılıyor muyum?",
        ],
      },
    ],
    sources: [
      {
        label: "Directory of Visa Categories",
        organization: "U.S. Department of State",
        href: "https://travel.state.gov/content/travel/en/us-visas/visa-information-resources/all-visa-categories.html",
      },
      {
        label: "Visitor Visa",
        organization: "U.S. Department of State",
        href: "https://travel.state.gov/content/travel/en/us-visas/tourism-visit/visitor.html",
      },
      {
        label: "Temporary Worker Visas",
        organization: "U.S. Department of State",
        href: "https://travel.state.gov/content/travel/en/us-visas/employment/temporary-worker-visas.html",
      },
      {
        label: "Working in the United States",
        organization: "USCIS",
        href: "https://www.uscis.gov/working-in-the-united-states",
      },
    ],
    relatedSlugs: ["amerika-is-basvurusu-cv-rehberi", "vize-bulteni-priority-date-okuma", "ds-160-vize-mulakati-hazirlik"],
  },
  {
    slug: "vize-bulteni-priority-date-okuma",
    category: "Vize Bülteni",
    tone: "blue",
    title: "Visa Bulletin Nasıl Okunur? Priority Date ve Final Action Date Rehberi",
    seoTitle: "Visa Bulletin: Priority Date Nasıl Okunur?",
    shortTitle: "Priority Date ve Visa Bulletin'i oku",
    description: "ABD Visa Bulletin tablolarında priority date, Final Action Dates, Dates for Filing, C ve U ifadelerinin ne anlama geldiğini öğren.",
    readMinutes: 7,
    updated: "22 Ağustos 2026",
    featured: false,
    outcomes: [
      "Doğru tercih kategorisi ve ülke sütununu bulacaksın",
      "Final Action Dates ile Dates for Filing farkını anlayacaksın",
      "Priority date'ini kesim tarihiyle doğru yönde karşılaştıracaksın",
    ],
    sections: [
      {
        id: "neden-bulten",
        title: "1. Visa Bulletin neden var?",
        paragraphs: [
          "Bazı aile ve istihdam temelli göçmen vize kategorileri yıllık sayısal sınırlara tabidir. Talep mevcut vize sayısını aştığında sıra oluşur. ABD Dışişleri Bakanlığı aylık Visa Bulletin ile hangi öncelik tarihlerine kadar işlem yapılabildiğini gösterir.",
          "Bülten bir dosyanın onaylandığını veya kesin vize verileceğini göstermez; yalnızca vize numarası bulunabilirliği açısından sıranın hangi noktada olduğunu anlamaya yardım eder.",
        ],
      },
      {
        id: "priority-date",
        title: "2. Priority Date'ini bul",
        paragraphs: [
          "Priority Date, dosyanın sıradaki yerini belirleyen tarihtir. Tarihin hangi belgeden alınacağı kategoriye göre değişebilir; çoğu aile dilekçesinde USCIS'in dilekçeyi aldığı tarih, bazı istihdam süreçlerinde ise labor certification başvuru tarihi temel alınabilir.",
          "I-797 bildiriminde veya ilgili resmi dosya belgelerinde yazan tarihi esas al. Emin değilsen dosyanı yürüten yetkili uzman ya da resmi kurum talimatı ile doğrula.",
        ],
      },
      {
        id: "iki-tablo",
        title: "3. İki tabloyu birbirine karıştırma",
        paragraphs: [
          "Final Action Dates tablosu, bir göçmen vize numarasının nihai işlem için ne zaman kullanılabilir olabileceğini gösterir. Dates for Filing tablosu ise belirli başvuru sahiplerinin belgelerini daha erken hazırlayıp sunabilmesine ilişkin ayrı bir eşiği gösterir.",
          "ABD içinde adjustment of status başvurusu yapacak kişiler, USCIS'in o ay hangi tabloya izin verdiğini ayrıca USCIS Visa Bulletin sayfasından kontrol etmelidir. Konsolosluk sürecinde National Visa Center talimatı ve Dışişleri Bakanlığı bülteni izlenir.",
        ],
      },
      {
        id: "karsilastirma",
        title: "4. Tarihleri doğru karşılaştır",
        paragraphs: [
          "Önce aile veya istihdam kategorini, sonra doğum ülken için geçerli sütunu bul. Türkiye çoğu tabloda ayrı listelenmediği için genellikle “All Chargeability Areas Except Those Listed” sütunu kullanılır; ancak her ay bülteni yeniden kontrol et.",
          "Priority Date'in tabloda yazan kesim tarihinden daha erkense, ilgili tablo açısından current olabilir. Priority Date daha sonraysa sıra henüz gelmemiştir.",
        ],
        bullets: [
          "C (Current): Kategori için o tabloda bir kesim tarihi yoktur.",
          "U (Unauthorized): O ay için vize numarası kullanımına izin verilmez.",
          "Belirli tarih: Yalnızca o tarihten daha eski priority date'ler kapsamdadır.",
        ],
      },
      {
        id: "retrogression",
        title: "5. Tarih geriye giderse ne olur?",
        paragraphs: [
          "Talep arttığında veya yıllık sayı sınırına yaklaşıldığında kesim tarihleri gerileyebilir; buna retrogression denir. Bir ay current görünen dosya sonraki ay yeniden beklemeye geçebilir.",
          "Bu nedenle tek bir ekran görüntüsünü saklayıp ona güvenmek yerine bülteni her ay kontrol et. Dosyanla ilgili işlem zamanı için USCIS, National Visa Center veya konsolosluğun doğrudan talimatını esas al.",
        ],
      },
    ],
    sources: [
      {
        label: "Current and Archived Visa Bulletins",
        organization: "U.S. Department of State",
        href: "https://travel.state.gov/content/travel/en/legal/visa-law0/visa-bulletin.html",
      },
      {
        label: "Visa Availability and Priority Dates",
        organization: "USCIS",
        href: "https://www.uscis.gov/green-card/green-card-processes-and-procedures/visa-availability-priority-dates",
      },
      {
        label: "Adjustment of Status Filing Charts",
        organization: "USCIS",
        href: "https://www.uscis.gov/visabulletininfo",
      },
    ],
    relatedSlugs: ["abd-vize-turleri-karsilastirma", "amerika-is-basvurusu-cv-rehberi"],
  },
  {
    slug: "ds-160-vize-mulakati-hazirlik",
    category: "Vize Başvurusu",
    tone: "red",
    title: "DS-160 ve ABD Vize Mülakatı Hazırlık Rehberi",
    seoTitle: "DS-160 ve ABD Vize Mülakatı Rehberi",
    shortTitle: "DS-160 ve vize mülakatına hazırlan",
    description: "DS-160 formunu doğru doldurma, confirmation page, gerekli belgeler ve ABD vize mülakatına tutarlı hazırlanma adımlarını öğren.",
    readMinutes: 8,
    updated: "22 Ağustos 2026",
    featured: false,
    outcomes: [
      "DS-160 öncesi gereken bilgileri tek yerde toplayacaksın",
      "Form, randevu ve belge adımlarını birbirinden ayıracaksın",
      "Mülakatta kısa, doğru ve tutarlı cevaplar vermeye hazırlanacaksın",
    ],
    sections: [
      {
        id: "ds160-oncesi",
        title: "1. DS-160'a başlamadan önce bilgi dosyası hazırla",
        paragraphs: [
          "DS-160 göçmen olmayan vize başvurularında kullanılan çevrimiçi formdur. Formu göndermek başvuru sürecinin ilk adımıdır; randevu ve yerel konsolosluk adımları ayrıca tamamlanır.",
          "Resmi DS-160 SSS sayfası pasaport, varsa seyahat planı, geçmiş ABD seyahatleri, son yıllardaki uluslararası seyahat geçmişi, eğitim ve çalışma bilgileri gibi verilerin hazır tutulmasını önerir. Öğrenci, değişim ve dilekçe temelli çalışma kategorilerinde ek numara ve belgeler gerekebilir.",
        ],
      },
      {
        id: "dogruluk",
        title: "2. Her cevabı doğru ve eksiksiz gir",
        paragraphs: [
          "Yanlış veya eksik cevaplar formun düzeltilmesini ve görüşmenin yeniden planlanmasını gerektirebilir. Formdaki çoğu cevap İngilizce ve İngilizce karakterlerle yazılır; yalnızca sistemin özellikle istediği yerlerde yerel alfabe kullanılır.",
          "Bilmediğin bir tarihi uydurma. Resmi belgeleri, eski pasaportları ve iş/okul kayıtlarını kontrol et. Bir kişi yardımcı oluyorsa bu yardım formda gerektiği şekilde belirtilmeli; son cevapların doğruluğu başvuru sahibinin sorumluluğundadır.",
        ],
        callout: "DS-160'taki bilgiler, randevu kaydı, sunduğun belgeler ve mülakattaki cevapların birbiriyle tutarlı olmalı.",
      },
      {
        id: "confirmation-page",
        title: "3. Application ID ve Confirmation Page'i sakla",
        paragraphs: [
          "Form üzerinde çalışırken Application ID'yi kaydet. Gönderimden sonra barkodlu Confirmation Page'i yazdır veya güvenli PDF olarak sakla. Resmi talimata göre tüm formun çıktısı yerine confirmation page mülakatta gereklidir.",
          "Her vize başvuru sahibinin, pasaportta ailece kayıtlı olunsa bile ayrı bir başvurusu olabilir. Başvurduğun konsolosluğun randevu ve belge talimatlarını ayrıca kontrol et.",
        ],
      },
      {
        id: "belgeler",
        title: "4. Genel belge setini kategoriye özel listeyle tamamla",
        paragraphs: [
          "Ziyaretçi vizesi için resmi sayfa genel olarak seyahate uygun pasaport, DS-160 confirmation page, gerekiyorsa ücret makbuzu ve fotoğraf talimatlarını listeler. Öğrenci, değişim, çalışma veya göçmen vize kategorilerinde ek belgeler gerekir.",
          "Genel internet listesi yerine başvuru yapacağın ABD Büyükelçiliği veya Konsolosluğunun güncel sayfasını ve kategoriye özel resmi talimatı esas al.",
        ],
        bullets: [
          "Pasaport ve varsa eski pasaportlar",
          "DS-160 Confirmation Page ve randevu onayı",
          "Kategoriye özgü okul, program, petition veya aile belgeleri",
          "Yerel konsolosluğun istediği fotoğraf ve ek dokümanlar",
        ],
      },
      {
        id: "mulakat",
        title: "5. Ezber değil, tutarlılık çalış",
        paragraphs: [
          "Mülakatın amacı başvurudaki bilgileri ve vize kategorisine uygunluğu değerlendirmektir. İnternetteki “garanti cevapları” ezberlemek yerine seyahat amacını, planlanan süreyi, finansmanı ve kişisel durumunu doğru ve kısa biçimde anlatmaya hazırlan.",
          "Sorulanı dinle, doğrudan cevapla ve bilmediğin konuda tahmin yürütme. Vize verilene kadar geri ödemesiz bilet veya kesin seyahat planı yapmamak daha güvenlidir.",
        ],
      },
    ],
    sources: [
      {
        label: "DS-160 Frequently Asked Questions",
        organization: "U.S. Department of State",
        href: "https://travel.state.gov/content/travel/en/us-visas/visa-information-resources/forms/ds-160-online-nonimmigrant-visa-application/ds-160-faqs.html",
      },
      {
        label: "Visitor Visa — Required Documentation",
        organization: "U.S. Department of State",
        href: "https://travel.state.gov/content/travel/en/us-visas/tourism-visit/visitor.html",
      },
      {
        label: "Visa Photo Requirements",
        organization: "U.S. Department of State",
        href: "https://travel.state.gov/content/travel/en/us-visas/visa-information-resources/photos.html",
      },
      {
        label: "Find a U.S. Embassy or Consulate",
        organization: "U.S. Department of State",
        href: "https://travel.state.gov/content/travel/en/us-visas/visa-information-resources/list-of-posts.html",
      },
    ],
    relatedSlugs: ["abd-vize-turleri-karsilastirma", "gocmenlik-dolandiriciliklarindan-korunma"],
  },
  {
    slug: "gocmenlik-dolandiriciliklarindan-korunma",
    category: "Güvenlik",
    tone: "navy",
    title: "Green Card ve ABD Vize Dolandırıcılıklarından Korunma Rehberi",
    seoTitle: "Vize Dolandırıcılıklarından Korunma Rehberi",
    shortTitle: "Göçmenlik dolandırıcılıklarını fark et",
    description: "Sahte Green Card mesajları, kopya devlet siteleri, yetkisiz danışmanlar ve şüpheli ödeme taleplerini ayırt etmek için güvenlik kontrol listesi.",
    readMinutes: 6,
    updated: "22 Ağustos 2026",
    featured: false,
    outcomes: [
      "Resmi .gov adresleri ile kopya siteleri ayırt edeceksin",
      "Sahte seçildiniz mesajları ve şüpheli ödeme yöntemlerini tanıyacaksın",
      "Hukuki yardım verebilecek yetkili kişileri doğrulayabileceksin",
    ],
    sections: [
      {
        id: "resmi-adres",
        title: "1. Alan adını her işlemde yeniden kontrol et",
        paragraphs: [
          "ABD devlet kurumlarının ana siteleri .gov ile biter. USCIS işlemlerinde uscis.gov, konsolosluk ve vize bilgilerinde travel.state.gov, Diversity Visa sonuçlarında dvprogram.state.gov adreslerini doğrudan yazarak açmak en güvenli yöntemdir.",
          "Logo, bayrak veya devlet mührü görmek sitenin resmi olduğunu kanıtlamaz. Reklam sonuçlarındaki benzer yazımlı alan adlarına ve devlet formu için yüksek ücret isteyen sayfalara dikkat et.",
        ],
      },
      {
        id: "dv-mesajlari",
        title: "2. “Green Card kazandınız” e-postasına güvenme",
        paragraphs: [
          "Diversity Visa seçimi yalnızca resmi Entrant Status Check ekranından Confirmation Number kullanılarak kontrol edilir. ABD Dışişleri Bakanlığı seçilenleri e-posta veya mektupla bildirmez ve başka bir şirket sonuç bildirmeye yetkili değildir.",
          "DV programına kayıt ücretsizdir. Seçilen kişiler sonraki resmi vize işlemlerinde ücretlerle karşılaşabilir; ancak kayıt ücreti veya sonucu açıklama ücreti isteyen mesajlar kırmızı bayraktır.",
        ],
      },
      {
        id: "odeme",
        title: "3. Ödeme yöntemini sorgula",
        paragraphs: [
          "USCIS, göçmenlik ücretini bir kişiye telefon veya e-posta üzerinden Western Union, MoneyGram, PayPal ya da hediye kartıyla göndermenizi istemediğini açıkça belirtir. Güncel ücret ve ödeme yöntemini her zaman resmi form sayfasından kontrol et.",
          "Acil ödeme baskısı, gizlilik talebi, kişisel hesaba havale ve “özel devlet bağlantısı” iddiası dolandırıcılık işareti olabilir.",
        ],
      },
      {
        id: "yetkili-yardim",
        title: "4. Hukuki yardım verecek kişinin yetkisini doğrula",
        paragraphs: [
          "ABD göçmenlik konusunda hukuki tavsiye, lisanslı bir avukat veya Department of Justice tarafından tanınan bir kuruluşta çalışan akredite temsilci tarafından verilmelidir. Belge hazırlayan diğer kişiler form doldurmaya yardımcı olabilir; ancak seçeneklerini yorumlayıp hukuki tavsiye veremez.",
          "Boş form imzalama, yanlış bilgi içeren belgeyi onaylama ve resmi kurum özellikle istemedikçe orijinal belgeni bırakma. Ödeme yaptıysan makbuz, hazırlanan formların kopyası ve yazışmaları sakla.",
        ],
      },
      {
        id: "suphe-durumu",
        title: "5. Şüpheli bir durum yaşadıysan",
        paragraphs: [
          "İletişimi kes, yeni ödeme yapma ve hesap şifrelerini değiştir. Paylaştığın bilgilere göre bankanla, e-posta sağlayıcınla ve ilgili resmi kurumla iletişime geç. USCIS'in scam bildirim sayfası ve Federal Trade Commission şikâyet kanalları kullanılabilir.",
        ],
        bullets: [
          "Mesaj, e-posta başlığı, ödeme dekontu ve web adresinin ekran görüntüsünü sakla.",
          "USCIS hesabına yalnızca my.uscis.gov üzerinden gir.",
          "Kimlik veya pasaport bilgisi paylaştıysan hesap güvenliği ve kimlik hırsızlığı adımlarını değerlendir.",
          "Kişisel hukuki risk varsa yetkili bir avukata danış.",
        ],
      },
    ],
    sources: [
      {
        label: "Avoid Scams",
        organization: "USCIS",
        href: "https://www.uscis.gov/scams-fraud-and-misconduct/avoid-scams",
      },
      {
        label: "Find Legal Services",
        organization: "USCIS",
        href: "https://www.uscis.gov/scams-fraud-and-misconduct/avoid-scams/find-legal-services",
      },
      {
        label: "Diversity Visa — Selection of Applicants",
        organization: "U.S. Department of State",
        href: "https://travel.state.gov/content/travel/en/us-visas/immigrate/diversity-visa-program-entry/diversity-visa-submit-entry1/diversity-visa-selection-of-applicants.html",
      },
      {
        label: "Report Fraud",
        organization: "Federal Trade Commission",
        href: "https://reportfraud.ftc.gov/",
      },
    ],
    relatedSlugs: ["green-card-cekilisi-basvuru-rehberi", "ds-160-vize-mulakati-hazirlik"],
  },
];

export const featuredArticles = articles.filter((article) => article.featured);

export function findArticleBySlug(slug: string) {
  return articles.find((article) => article.slug === slug);
}

export function findArticleByPath(pathname: string) {
  const normalized = pathname.replace(/\/+$/, "");
  const match = normalized.match(/^\/rehberler\/([^/]+)$/);
  return match ? findArticleBySlug(match[1]) : undefined;
}
